import React from "react";
import { useTranslation } from "react-i18next";
import { TransformedProduct } from "../../../product/types/productApiTypes";
import { useMoveSavedForLaterItemBackToCart } from "../../hooks/useMoveSavedForLaterItemBackToCart";
import { useRemoveFromSavedForLater } from "../../hooks/useRemoveFromSavedForLater";
import { useMoveSavedForLaterItemToWishlist } from "../../hooks/useMoveSavedForLaterItemToWishlist";
import { useToggleCompareItem } from "../../../compare/hooks/useToggleCompareItem";
import CartItemActions from "../../../cart/components/common/CartItemActions";

interface PageSavedForLaterItemProps {
  item: TransformedProduct;
}

const PageSavedForLaterItem: React.FC<PageSavedForLaterItemProps> = ({
  item,
}) => {
  const { t } = useTranslation();

  const { handleMoveSavedForLaterItemBackToCart, isMoving: isMovingToCart } =
    useMoveSavedForLaterItemBackToCart();

  const { handleRemoveFromSavedForLater, isRemoving } =
    useRemoveFromSavedForLater();

  const {
    handleMoveSavedForLaterItemToWishlist,
    isMoving: isMovingToWishlist,
  } = useMoveSavedForLaterItemToWishlist();

  // Use item.id (Variation ID for saved variations) for comparison
  const { handleToggle: handleCompareToggle, isInCompare } =
    useToggleCompareItem(item.id);

  const isLoading = isMovingToCart || isRemoving || isMovingToWishlist;

  // --- 1. Move to Cart ---
  const handleAddToCartClick = () => {
    handleMoveSavedForLaterItemBackToCart(item.id);
  };

  // --- 2. Move to Wishlist ---
  const handleMoveToWishlistClick = () => {
    // Pass parentId to ensure the main product is wishlisted if this is a variation
    handleMoveSavedForLaterItemToWishlist(item.id, item.parentId);
  };

  // --- 3. Render Attributes (Text Only, matching Cart) ---
  const renderAttributes = () => {
    if (!item.enrichedAttributes || item.enrichedAttributes.length === 0)
      return null;

    return (
      <div className="page-saved-for-later-item__meta">
        {item.enrichedAttributes.map((attr) => {
          const term = attr.terms[0];
          if (!term) return null;

          const termName = term.name;

          return (
            <div key={`${attr.taxonomy}-${term.id}`}>
              <strong>{attr.name}:</strong> {termName}
            </div>
          );
        })}
      </div>
    );
  };

  const image = item.images && item.images.length > 0 ? item.images[0] : null;

  return (
    <div className="page-saved-for-later-item-wrapper">
      {/* Main content row for the item */}
      <div className="page-saved-for-later-item">
        {/* Image Column */}
        <div className="page-saved-for-later-item__image-col">
          <a
            href={item.permalink}
            className="page-saved-for-later-item__image-link"
          >
            {image ? (
              <img src={image.thumbnail} alt={image.alt || item.name} />
            ) : (
              <div className="placeholder-image" />
            )}
          </a>
        </div>

        {/* Details Column */}
        <div className="page-saved-for-later-item__details-col">
          <a href={item.permalink} className="page-saved-for-later-item__name">
            {item.name}
          </a>

          {renderAttributes()}

          {/* Stock Status */}
          <div className="page-saved-for-later-item__stock-status">
            {item.stockStatus === "outofstock" ? (
              <span className="is-out-of-stock">
                {t("productCard.outOfStockBadge", "Sold Out")}
              </span>
            ) : (
              <span className="is-in-stock">
                {t("productInfoDetails.inStock", "In Stock")}
              </span>
            )}
          </div>
        </div>

        {/* Price & Action Column */}
        <div className="page-saved-for-later-item__action-col">
          <div className="page-saved-for-later-item__price-wrapper">
            {item.onSale ? (
              <>
                <del className="page-saved-for-later-item__regular-price">
                  {item.formattedRegularPrice}
                </del>
                <span className="page-saved-for-later-item__sale-price">
                  {item.formattedPrice}
                </span>
              </>
            ) : (
              <span className="page-saved-for-later-item__price">
                {item.formattedPrice}
              </span>
            )}
          </div>

          <div className="page-saved-for-later-item__buttons">
            <button
              className="page-saved-for-later-item__btn page-saved-for-later-item__btn--move"
              onClick={handleAddToCartClick}
              disabled={isLoading || item.stockStatus === "outofstock"}
            >
              {t("savedForLater.moveToCart", "Add Back to Cart")}
            </button>

            <button
              className="page-saved-for-later-item__btn page-saved-for-later-item__btn--remove"
              onClick={() => handleRemoveFromSavedForLater(item.id)}
              disabled={isLoading}
            >
              {t("savedForLater.remove", "Remove")}
            </button>
          </div>
        </div>
      </div>

      {/* 
          Secondary Actions (Move to Wishlist & Compare)
          Placed at the bottom
      */}
      <CartItemActions
        onMoveToWishlist={handleMoveToWishlistClick}
        onToggleCompare={handleCompareToggle}
        isInCompare={isInCompare}
        isMoving={isMovingToWishlist}
        itemName={item.name}
        context="page"
        placement="bottom"
      />
    </div>
  );
};

export default PageSavedForLaterItem;
